import {useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {QueryStatus} from '@reduxjs/toolkit/dist/query'
import {useCachedSearchTopOffers} from 'components/Accommodation/Offers/useCachedSearchTopOffers'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {getMeta} from 'modules/meta/selectors'
import {
  getFilteredRoomsData,
  getHotelApiRoomsParameters
} from 'modules/searchApi/selectors'

import {
  getIsAuthenticated,
  selectIsLoading
} from '@daedalus/core/src/auth/modules/selectors'
import {getPersistedConfiguration} from '@daedalus/core/src/offer/services/offerConfigurationApi/store'
import {makeTransformSapiRoomsResponse} from '@daedalus/core/src/room/business/transformSapiRoomsResponse'
import {useGetRoomsQuery} from '@daedalus/core/src/sapi/services/searchApi'
import {getIsUserEligibleForDealFreeze} from '@daedalus/shared/src/dealFreeze/modules/selector'
import {setFirstEligibleDealFreezeRoom} from '@daedalus/shared/src/dealFreeze/modules/slice'
import {isOfferEligibleForDealFreeze} from '@daedalus/shared/src/dealFreeze/utils/OfferEligibility'

export const useGetRooms = () => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const roomsEndpointParams = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isUserEligibleForDealFreeze = useSelector(
    getIsUserEligibleForDealFreeze
  )
  const dispatch = useDispatch()
  const isAuthLoading = useSelector(selectIsLoading)
  // We need to replicate the offers/sb ordering here in the same way as we do when rendering top-offers
  const {isComplete} = useCachedSearchTopOffers({hotelId})
  const isVioOfferSelected = isVioOfferOrBundleSelected()
  const offersLoadedOrVioOfferSelected = isComplete || isVioOfferSelected
  const {rooms, checkIn} = roomsEndpointParams
  const configuration = getPersistedConfiguration()
  const eligibilityStatus = !isAuthenticated || isUserEligibleForDealFreeze
  const shouldSkip =
    !hotelId ||
    isAuthLoading ||
    // When reloading AP and rooms are optimized we wait for optimized roomsConfig
    !rooms ||
    // skip when top-offers are not loaded yet for this hotel and it's not a vio-clicker scenario
    !offersLoadedOrVioOfferSelected
  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const transformResponse = makeTransformSapiRoomsResponse({
    includeTaxes,
    includeLocalTaxes,
    /**
     * Hacky way to clear isClicked flag from returned rooms-offers
     * when there is no selected offer params in the url, but rooms-offers needs to be called with
     * clickedOffer data to prevent price mismatch between top-offers and rooms-offers shown together on AP
     **/
    clearIsClickedFlag: !isVioOfferSelected
  })

  const result = useGetRoomsQuery(
    {
      transformResponse,
      ...roomsEndpointParams
    },
    {
      skip: shouldSkip
    }
  )

  useEffect(() => {
    // A logic to show DealFreezeBanner on First Deal Freeze room
    if (result.status === QueryStatus.fulfilled) {
      let eligibleOfferIndex = null
      const {rooms} = result.data

      for (let roomIndex = 0; roomIndex < rooms.length; roomIndex++) {
        const {offers, id: roomId} = rooms[roomIndex]
        for (let offerIndex = 0; offerIndex < offers.length; offerIndex++) {
          const isOfferEligible =
            isOfferEligibleForDealFreeze(
              offers[offerIndex],
              checkIn,
              new Date().toString(),
              configuration.dealFreeze
            ) && eligibilityStatus

          if (isOfferEligible) {
            eligibleOfferIndex = offerIndex
            dispatch(setFirstEligibleDealFreezeRoom(roomId))
            break
          }
        }

        if (eligibleOfferIndex !== null) {
          break
        }
      }
    }
  }, [
    checkIn,
    configuration.dealFreeze,
    dispatch,
    eligibilityStatus,
    result.data,
    result.status
  ])

  const filteredData = useSelector(getFilteredRoomsData)

  return useMemo(
    () => ({
      ...result,
      ...filteredData,
      data: filteredData
    }),
    [filteredData, result]
  )
}
